import React, { useEffect, useState } from "react";
import {
  User,
  CheckCircle,
  RefreshCw,
  AlertCircle,
  ArrowLeft,
  X,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

function UserManager() {
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const [formData, setFormData] = useState({
    phone_number: "",
    full_name: "",
    email: "",
    password: "",
    company: "",
    project: "",
    role: "",
    org_id: "",
    project_id: "",
  });
  const [organizations, setOrganizations] = useState([]);
  const [projects, setProjects] = useState([]);
  const [roles, setRoles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [responseMessage, setResponseMessage] = useState("");
  const [responseType, setResponseType] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        const res = await fetch(`${BASE_URL}/orgdetails/getorganizations`);
        if (!res.ok) throw new Error("Failed to fetch organizations");
        const data = await res.json();
        setOrganizations(data);
      } catch (err) {
        console.error(err);
      }
    };

    const fetchRoles = async () => {
      try {
        const res = await fetch(`${BASE_URL}/orgdetails/roles`);
        if (!res.ok) throw new Error("Failed to fetch roles");
        const data = await res.json();
        setRoles(data);
      } catch (err) {
        console.error(err);
      }
    };

    fetchOrganizations();
    fetchRoles();
  }, [BASE_URL]);

  useEffect(() => {
    if (!formData.company) return;
    const fetchProjects = async () => {
      try {
        const res = await fetch(`${BASE_URL}/orgdetails/projects`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ org_id: formData.company }),
        });
        if (!res.ok) throw new Error("Failed to fetch projects");
        const data = await res.json();
        setProjects(data);
      } catch (err) {
        console.error(err);
      }
    };
    fetchProjects();
  }, [formData.company, BASE_URL]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "company") {
      setFormData((prev) => ({
        ...prev,
        company: value,
        project: "",
        project_id: "",
        org_id: value,
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const res = await fetch(`${BASE_URL}/users/addUser`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      if (!res.ok) {
        const errorData = await res.json();
        setResponseMessage("Failed to add user.");
        setResponseType("error");
        throw new Error(errorData.message || "Something went wrong");
      }
      await res.json();
      setFormData({
        phone_number: "",
        full_name: "",
        email: "",
        password: "",
        company: "",
        project: "",
        role: "",
        org_id: "",
        project_id: "",
      });
      setResponseMessage("User added successfully!");
      setResponseType("success");
    } catch (err) {
      console.error(err);
      // In case responseMessage was not set in the error branch
      if (!responseMessage) {
        setResponseMessage("Failed to add user.");
        setResponseType("error");
      }
    } finally {
      setIsSubmitting(false);
      setShowModal(true);
    }
  };

  // Modal Component for response messages
  const ResponseModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-semibold text-gray-900">
            {responseType === "success" ? "Success!" : "Error"}
          </h3>
          <button
            onClick={() => setShowModal(false)}
            className="text-gray-400 hover:text-gray-500"
          >
            <X className="h-6 w-6" />
          </button>
        </div>
        <div className="flex items-center justify-center mb-6">
          <div
            className={`rounded-full p-3 ${
              responseType === "success" ? "bg-green-100" : "bg-red-100"
            }`}
          >
            {responseType === "success" ? (
              <CheckCircle className="h-8 w-8 text-green-500" />
            ) : (
              <AlertCircle className="h-8 w-8 text-red-500" />
            )}
          </div>
        </div>
        <p className="text-center text-gray-600 mb-6">{responseMessage}</p>
        <button
          onClick={() => {
            setShowModal(false);
            if (responseType === "success") {
              navigate("/users/new");
            }
          }}
          className="w-full bg-blue-600 text-white rounded-lg py-3 hover:bg-blue-700 transition-colors"
        >
          {responseType === "success" ? "View All Users" : "Try Again"}
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <div className="p-2 bg-blue-50 rounded-lg">
                <User className="h-6 w-6 text-blue-600" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900">
                User Management
              </h1>
            </div>
            <button
              onClick={() => navigate("/users/new")}
              className="inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Return
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Add User</h2>
            <p className="mt-1 text-sm text-gray-500">
              Fill in the details to create a new user account.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
                Full Name
              </label>
              <input
                type="text"
                name="full_name"
                value={formData.full_name}
                onChange={handleChange}
                placeholder="John Doe"
                required
                className="block w-full rounded-lg border border-gray-300 px-4 py-3 text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:ring-blue-500 focus:outline-none transition-colors sm:text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
                Phone Number
              </label>
              <input
                type="text"
                name="phone_number"
                value={formData.phone_number}
                onChange={handleChange}
                placeholder="+1 (555) 123-4567"
                required
                className="block w-full rounded-lg border border-gray-300 px-4 py-3 text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:ring-blue-500 focus:outline-none transition-colors sm:text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
                Email Address
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="john@example.com"
                required
                className="block w-full rounded-lg border border-gray-300 px-4 py-3 text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:ring-blue-500 focus:outline-none transition-colors sm:text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
                Password
              </label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="••••••••"
                required
                className="block w-full rounded-lg border border-gray-300 px-4 py-3 text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:ring-blue-500 focus:outline-none transition-colors sm:text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
                Organization
              </label>
              <select
                name="company"
                value={formData.company}
                onChange={handleChange}
                required
                className="block w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-blue-500 focus:ring-blue-500 focus:outline-none transition-colors sm:text-sm"
              >
                <option value="">Select your organization</option>
                {organizations.map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.org_name}
                  </option>
                ))}
              </select>
            </div>

            {formData.company && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
                  Project
                </label>
                <select
                  name="project"
                  value={formData.project}
                  onChange={(e) => {
                    handleChange(e);
                    const selectedProject = projects.find(
                      (proj) => proj.project_name === e.target.value
                    );
                    if (selectedProject) {
                      setFormData((prev) => ({
                        ...prev,
                        project_id: selectedProject.id,
                      }));
                    }
                  }}
                  required
                  className="block w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-blue-500 focus:ring-blue-500 focus:outline-none transition-colors sm:text-sm"
                >
                  <option value="">Select your project</option>
                  {projects.map((proj) => (
                    <option key={proj.id} value={proj.project_name}>
                      {proj.project_name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
                Role
              </label>
              <select
                name="role"
                value={formData.role}
                onChange={handleChange}
                required
                className="block w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-blue-500 focus:ring-blue-500 focus:outline-none transition-colors sm:text-sm"
              >
                <option value="">Select your role</option>
                {roles.map((role) => (
                  <option key={role.id} value={role.role}>
                    {role.role}
                  </option>
                ))}
              </select>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full flex justify-center items-center px-6 py-3 border border-transparent rounded-lg text-base font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? (
                <RefreshCw className="h-5 w-5 animate-spin" />
              ) : (
                <>
                  <CheckCircle className="h-5 w-5 mr-2" />
                  Create Account
                </>
              )}
            </button>
          </form>
        </div>
      </main>
      {showModal && <ResponseModal />}
    </div>
  );
}

export default UserManager;
